import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { useDialogFocus } from "./ui/useDialogFocus";
import { useEscapeDelDialogo } from "./ui/useEscapeDelDialogo";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Título arriba del drawer. */
  title: string;
  /** Subtítulo opcional debajo del título (RUT, código, etc). */
  subtitle?: string;
  /** Icono opcional al lado del título. */
  icon?: LucideIcon;
  /** Ancho del drawer en desktop. Default 480px. */
  width?: "sm" | "md" | "lg";
  /** Contenido del cuerpo. Va en un contenedor scrolleable. */
  children: React.ReactNode;
  /** Acciones al pie. Se renderizan en una barra sticky. */
  footer?: React.ReactNode;
  /** Cerrar con click en backdrop. Default false (UX-16): un clic fuera no
   *  debe perder lo que se estaba editando. Esc y la X siempre cierran. */
  closeOnBackdrop?: boolean;
}

const WIDTH_CLS: Record<NonNullable<DrawerProps["width"]>, string> = {
  sm: "sm:max-w-[400px]",
  md: "sm:max-w-[480px]",
  lg: "sm:max-w-[640px]",
};

/**
 * Drawer lateral derecho — patrón estándar para detalle/edición sin
 * perder el contexto de la lista. La lista queda visible detrás
 * (mobile: full-screen). Backdrop con backdrop-blur, slide-in 220ms.
 * Esc cierra; el backdrop sólo con `closeOnBackdrop`. Foco (A11Y-11): al
 * abrir va al primer elemento enfocable del cuerpo (o al título), Tab queda
 * atrapado dentro y al cerrar vuelve al elemento que lo abrió.
 *
 * Ver design-system.md sección 6.5.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  width = "md",
  children,
  footer,
  closeOnBackdrop = false,
}: DrawerProps): JSX.Element | null {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useDialogFocus(open, panelRef, bodyRef, titleRef);

  useEscapeDelDialogo(open, titleId, onClose);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={closeOnBackdrop ? onClose : undefined}
        tabIndex={-1}
        className="absolute inset-0 bg-neutral-900/30 backdrop-blur-sm transition-opacity duration-200"
      />

      {/* Panel — slide desde la derecha */}
      <aside
        ref={panelRef}
        className={twMerge(
          "absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-xl ring-1 ring-neutral-200",
          "motion-safe:animate-[drawer-in_220ms_cubic-bezier(0.2,0.7,0.3,1)]",
          WIDTH_CLS[width],
        )}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
          <div className="flex items-start gap-3 min-w-0">
            {Icon && (
              <div className="grid place-items-center h-8 w-8 rounded-md bg-brand-50 text-brand-700 shrink-0">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0 leading-tight">
              <h2
                id={titleId}
                ref={titleRef}
                tabIndex={-1}
                className="text-base font-semibold text-neutral-900 truncate focus:outline-none"
              >
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs text-neutral-600 truncate mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar drawer"
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        {/* Body scrolleable */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer sticky con acciones */}
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
            {footer}
          </footer>
        )}
      </aside>

      {/* Keyframes inline — sin agregar a tailwind config para no
          contaminar otros proyectos. La animación es opcional vía
          motion-safe — usuarios con motion-reduction ven el drawer
          aparecer sin slide. */}
      <style>{`
        @keyframes drawer-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
